import { useState, FormEvent, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useI18n } from '../i18n';
import { PASSWORD_MIN } from '../utils/password';
import PasswordStrengthMeter from '../components/PasswordStrengthMeter';

const AUTH_API_URL = import.meta.env.VITE_WATTELZCE_API_URL || '/api/wattelzce';

type Status = 'form' | 'submitting' | 'success' | 'invalid-token';

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const token = params.get('token') || '';

  const [pwd, setPwd] = useState('');
  const [pwdConfirm, setPwdConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [status, setStatus] = useState<Status>(token ? 'form' : 'invalid-token');
  const [error, setError] = useState<string | null>(null);


  // Redirect to login after success (5s)
  useEffect(() => {
    if (status !== 'success') return;
    const timer = setTimeout(() => navigate('/login', { replace: true }), 5000);
    return () => clearTimeout(timer);
  }, [status, navigate]);

  const canSubmit = pwd.length >= PASSWORD_MIN && pwd === pwdConfirm && status === 'form';

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setStatus('submitting');
    try {
      const res = await fetch(`${AUTH_API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword: pwd }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        let msg = t('team_genericError');
        if (typeof data?.error === 'string') msg = data.error;
        else if (typeof data?.message === 'string' && !data?.success) msg = data.message;
        else if (data?.error && typeof data.error === 'object') {
          if (Array.isArray(data.error) && data.error[0]?.message) msg = String(data.error[0].message);
          else if (data.error.message) msg = String(data.error.message);
        }
        if (res.status >= 500) msg = t('team_serviceUnavailable');
        throw new Error(msg);
      }
      setStatus('success');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('team_networkError'));
      setStatus('form');
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden flex items-center justify-center px-4 py-10
                    bg-gradient-to-br from-zce-navy via-zce-navy-light to-zce-navy zce-gradient-anim">
      {/* Animated background orbs */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-blue-400/20 rounded-full blur-3xl zce-float-orb pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[28rem] h-[28rem] bg-purple-400/20 rounded-full blur-3xl zce-float-orb pointer-events-none"
           style={{ animationDelay: '2s' }} />
      <div className="absolute top-1/2 left-1/3 w-72 h-72 bg-cyan-400/10 rounded-full blur-3xl zce-float-orb pointer-events-none"
           style={{ animationDelay: '4s' }} />

      <div className="relative w-full max-w-md zce-fade-in">
        <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl shadow-black/20 border border-white/40 overflow-hidden">
          <div className="h-1.5 bg-gradient-to-r from-blue-500 via-purple-500 to-cyan-500" />

          <div className="p-8 sm:p-10">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl mb-5
                              bg-gradient-to-br from-zce-navy to-zce-navy-light shadow-lg shadow-zce-navy/30">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-zce-navy tracking-tight">{t('team_reset_title')}</h1>
              <p className="text-zce-gray-muted mt-2 text-sm">{t('team_reset_subtitle')}</p>
            </div>

            {/* ── Invalid token ── */}
            {status === 'invalid-token' && (
              <div className="space-y-5">
                <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-center">
                  <div className="w-12 h-12 rounded-full bg-red-100 mx-auto flex items-center justify-center mb-3">
                    <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                  <h3 className="font-bold text-red-900 mb-1">{t('team_reset_invalidTitle')}</h3>
                  <p className="text-sm text-red-700">{t('team_reset_invalidText')}</p>
                </div>
                <Link to="/login"
                      className="block w-full text-center py-3 px-4 rounded-xl text-sm font-bold text-white
                                 bg-gradient-to-r from-zce-navy to-zce-navy-light hover:from-zce-navy-dark hover:to-zce-navy
                                 shadow-md hover:shadow-lg transition-all duration-200
                                 transform hover:scale-[1.02] active:scale-[0.98]">
                  {t('team_backToLogin')}
                </Link>
              </div>
            )}

            {/* ── Success ── */}
            {status === 'success' && (
              <div className="space-y-5">
                <div className="flex justify-center">
                  <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center">
                    <svg className="w-10 h-10 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
                <div className="text-center">
                  <h3 className="text-lg font-bold text-zce-navy mb-2">{t('team_reset_successTitle')}</h3>
                  <p className="text-sm text-zce-gray-muted">
                    {t('team_reset_successText')}
                  </p>
                  <p className="text-xs text-gray-400 mt-3">{t('team_reset_redirecting')}</p>
                </div>
                <Link to="/login"
                      className="block w-full text-center py-3 px-4 rounded-xl text-sm font-bold text-white
                                 bg-gradient-to-r from-zce-navy to-zce-navy-light hover:from-zce-navy-dark hover:to-zce-navy
                                 shadow-md hover:shadow-lg transition-all duration-200
                                 transform hover:scale-[1.02] active:scale-[0.98]">
                  {t('team_reset_signInNow')}
                </Link>
              </div>
            )}

            {/* ── Form ── */}
            {(status === 'form' || status === 'submitting') && (
              <form onSubmit={handleSubmit} className="space-y-5">
                {error && (
                  <div className="p-3.5 rounded-xl bg-red-50 border border-red-200 flex items-start gap-2.5">
                    <svg className="w-5 h-5 text-red-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-red-700 text-sm font-medium">{error}</span>
                  </div>
                )}

                {/* New password */}
                <div>
                  <label htmlFor="new-pwd" className="block text-xs font-semibold text-zce-navy mb-1.5 uppercase tracking-wide">
                    {t('team_reset_newPwd')}
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3.5 text-zce-gray-muted pointer-events-none">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </span>
                    <input
                      id="new-pwd"
                      type={showPwd ? 'text' : 'password'}
                      value={pwd}
                      onChange={(e) => setPwd(e.target.value)}
                      required
                      autoFocus
                      disabled={status === 'submitting'}
                      className="w-full pl-11 pr-12 py-3 bg-gray-50/80 border border-gray-200 rounded-xl text-sm text-zce-navy
                                 focus:bg-white focus:ring-2 focus:ring-zce-navy/30 focus:border-zce-navy
                                 transition-all duration-200 disabled:opacity-50 placeholder:text-gray-400"
                      placeholder="••••••••"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      tabIndex={-1}
                      onClick={() => setShowPwd((s) => !s)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-zce-gray-muted hover:text-zce-navy transition-colors"
                      aria-label={showPwd ? t('team_hidePwd') : t('team_showPwd')}
                    >
                      {showPwd ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>

                  {/* Strength meter */}
                  <PasswordStrengthMeter password={pwd} />
                </div>

                {/* Confirm */}
                <div>
                  <label htmlFor="confirm-pwd" className="block text-xs font-semibold text-zce-navy mb-1.5 uppercase tracking-wide">
                    {t('team_reset_confirmPwd')}
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 flex items-center pl-3.5 text-zce-gray-muted pointer-events-none">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    </span>
                    <input
                      id="confirm-pwd"
                      type={showPwd ? 'text' : 'password'}
                      value={pwdConfirm}
                      onChange={(e) => setPwdConfirm(e.target.value)}
                      required
                      disabled={status === 'submitting'}
                      className={`w-full pl-11 pr-4 py-3 bg-gray-50/80 border rounded-xl text-sm text-zce-navy
                                 focus:bg-white focus:ring-2 transition-all duration-200 disabled:opacity-50 placeholder:text-gray-400
                                 ${pwdConfirm && pwd !== pwdConfirm
                                    ? 'border-red-300 focus:ring-red-300 focus:border-red-400'
                                    : 'border-gray-200 focus:ring-zce-navy/30 focus:border-zce-navy'}`}
                      placeholder="••••••••"
                      autoComplete="new-password"
                    />
                  </div>
                  {pwdConfirm && pwd !== pwdConfirm && (
                    <p className="mt-1.5 text-xs text-red-600 font-medium">{t('team_reset_mismatch')}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={!canSubmit}
                  className="w-full py-3.5 px-4 rounded-xl text-sm font-bold text-white
                             bg-gradient-to-r from-zce-navy to-zce-navy-light
                             hover:from-zce-navy-dark hover:to-zce-navy
                             shadow-lg shadow-zce-navy/30 hover:shadow-xl hover:shadow-zce-navy/40
                             transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98]
                             disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none
                             flex items-center justify-center gap-2"
                >
                  {status === 'submitting' ? (
                    <>
                      <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      {t('team_reset_updating')}
                    </>
                  ) : (
                    <>
                      {t('team_reset_submit')}
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                    </>
                  )}
                </button>

                <div className="text-center pt-2">
                  <Link to="/login" className="text-xs font-semibold text-zce-navy-light hover:text-zce-navy hover:underline transition-colors">
                    ← {t('team_backToLogin')}
                  </Link>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
